/**
 * React Query hooks for the order plan's questions (XC-INV2PO answering note §4): the Inbox's
 * "Questions from Wingo" section and the sidebar's Inbox count read the same key, so the two never
 * disagree. An answer or an Undo re-reads the questions (a new "already made?" can appear at once: the
 * re-plan files it) and marks the rest of the Inbox stale.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  answerOrderQuestion,
  fetchOrderQuestions,
  undoOrderAnswer,
} from '@/services/orderQuestionsService';
import type {
  AnswerResult,
  HeldCountRow,
  LineCount,
  OrderQuestions,
  RecipeComponent,
  UndoResult,
} from '@/types/inv2po/orderQuestions';

export const orderQuestionKeys = {
  all: ['order-questions'] as const,
};

export function useOrderQuestions() {
  return useQuery<OrderQuestions>({
    queryKey: orderQuestionKeys.all,
    queryFn: fetchOrderQuestions,
    staleTime: 30_000,
  });
}

export interface AnswerVars {
  questionId: string;
  value: string;
  lineNos?: number[];
  /** "Ask me again in N weeks" (the pallets card). */
  weeks?: number;
  /** "Count it in my plan" (AW-144 slice 3): his count of each line. */
  counts?: LineCount[];
  /** A held recipe: every material of a case with its amount. */
  components?: RecipeComponent[];
  /** An order held on its lines: his products and how many of each ("Count it in my plan"). */
  products?: HeldCountRow[];
}

export function useAnswerOrderQuestion() {
  const qc = useQueryClient();
  return useMutation<AnswerResult, Error, AnswerVars>({
    mutationFn: ({ questionId, value, lineNos, weeks, counts, components, products }) =>
      answerOrderQuestion(questionId, value, lineNos ?? [], weeks, counts, components, products),
    onSettled: () => qc.invalidateQueries({ queryKey: orderQuestionKeys.all }),
  });
}

export function useUndoOrderAnswer() {
  const qc = useQueryClient();
  return useMutation<UndoResult, Error, string>({
    mutationFn: (answerId) => undoOrderAnswer(answerId),
    onSettled: () => qc.invalidateQueries({ queryKey: orderQuestionKeys.all }),
  });
}
